import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import moment from "moment";
import { v4 } from "uuid";
import application from "../core/application";
import { useModuleAccess } from "../settings/module-access";
import { Permission } from "../users/user.entity";
import { cashSessionRepository } from "./cash-session.repository";
import { CashSession } from "./cash-session.entity";
import { cashMovementRepository } from "./cash-movement.repository";
import { CashMovement } from "./cash-movement.entity";
import { sellRepository } from "../sell/sell.repository";
import type { Sell } from "../sell/sell.entity";

export type CashRegisterContextValue = {
  featureEnabled: boolean;
  /** O caixa existe como módulo, mas o usuário o desativou nas configurações. */
  disabledBySettings: boolean;
  loading: boolean;
  session: CashSession | null;
  /** Sessão aberta em dia anterior (obriga fechamento). */
  sessionExpired: boolean;
  canManage: boolean;
  movements: CashMovement[];
  sells: Sell[];
  refresh: () => Promise<void>;
  openSession: (openingCash: number) => Promise<void>;
  closeSession: (closingCash: number) => Promise<void>;
  addEntry: (amount: number, reason: string) => Promise<void>;
  addWithdrawal: (params: WithdrawalParams) => Promise<void>;
};

export type WithdrawalParams = {
  amount: number;
  reason: string;
  categoryId: string | null;
  categoryLabel: string | null;
  payableBillId?: string | null;
};

const CashRegisterContext = createContext<CashRegisterContextValue | null>(null);

export function CashRegisterProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [user] = application.user.useState();
  const [appSettings] = application.appSettings.useState();
  const moduleAccess = useModuleAccess();
  const disabledBySettings = appSettings.cashRegisterDisabled;
  const featureEnabled =
    !moduleAccess.loading && moduleAccess.isEnabled("cash_register") && !disabledBySettings;
  const canManage =
    user?.hasPermission(Permission.CASH_REGISTER_MANAGEMENT) ?? false;

  const [session, setSession] = useState<CashSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [movements, setMovements] = useState<CashMovement[]>([]);
  const [sells, setSells] = useState<Sell[]>([]);

  useEffect(() => {
    if (!featureEnabled || disabledBySettings) {
      setSession(null);
      setLoading(false);
      setMovements([]);
      setSells([]);
      return;
    }
    setLoading(true);
    const unsub = cashSessionRepository.subscribeOpenSession((s) => {
      setSession(s);
      setLoading(false);
    });
    return () => unsub();
  }, [featureEnabled, disabledBySettings]);

  useEffect(() => {
    if (!session) {
      setMovements([]);
      setSells([]);
      return;
    }
    Promise.all([
      cashMovementRepository.listBySession(session.id),
      sellRepository.getSellsByCashSessionId(session.id),
    ])
      .then(([ms, ss]) => {
        setMovements(ms);
        setSells(ss);
      })
      .catch(() => {
        setMovements([]);
        setSells([]);
      });
  }, [session]);

  const sessionExpired = useMemo(() => {
    if (!session || session.getStatus() !== "open") return false;
    return !moment(session.getOpenedAt()).isSame(moment(), "day");
  }, [session]);

  const refresh = useCallback(async () => {
    const s = await cashSessionRepository.findOpenSession();
    setSession(s);
    if (s) {
      const [ms, ss] = await Promise.all([
        cashMovementRepository.listBySession(s.id),
        sellRepository.getSellsByCashSessionId(s.id),
      ]);
      setMovements(ms);
      setSells(ss);
    } else {
      setMovements([]);
      setSells([]);
    }
  }, []);

  const openSession = useCallback(async (openingCash: number) => {
    const u = application.user.get();
    if (!u) throw new Error("Usuário não autenticado.");
    const existing = await cashSessionRepository.findOpenSession();
    if (existing) throw new Error("Já existe um caixa aberto.");
    const id = v4();
    const dayKey = moment().format("YYYY-MM-DD");
    const cs = new CashSession(
      id,
      "open",
      moment(),
      null,
      u.id,
      null,
      dayKey,
      Math.max(0, openingCash),
      null
    );
    await cashSessionRepository.save(cs);
    setSession(cs);
    setMovements([]);
    setSells([]);
  }, []);

  const closeSession = useCallback(async (closingCash: number) => {
    const u = application.user.get();
    if (!u) throw new Error("Usuário não autenticado.");
    const s = await cashSessionRepository.findOpenSession();
    if (!s) throw new Error("Nenhum caixa aberto.");
    s.setClosed(moment(), u.id, Math.max(0, closingCash));
    await cashSessionRepository.save(s);
    setSession(null);
    setMovements([]);
    setSells([]);
  }, []);

  const addEntry = useCallback(async (amount: number, reason: string) => {
    const u = application.user.get();
    if (!u) throw new Error("Usuário não autenticado.");
    const s = await cashSessionRepository.findOpenSession();
    if (!s) throw new Error("Nenhum caixa aberto para registrar entrada.");
    const movement = new CashMovement(
      v4(),
      s.id,
      "entry",
      Math.max(0, amount),
      reason.trim(),
      null,
      null,
      null,
      u.id,
      moment()
    );
    await cashMovementRepository.save(movement);
    setMovements((prev) => [...prev, movement]);
  }, []);

  const addWithdrawal = useCallback(async (params: WithdrawalParams) => {
    const u = application.user.get();
    if (!u) throw new Error("Usuário não autenticado.");
    const s = await cashSessionRepository.findOpenSession();
    if (!s) throw new Error("Nenhum caixa aberto para registrar retirada.");
    const movement = new CashMovement(
      v4(),
      s.id,
      "withdrawal",
      Math.max(0, params.amount),
      params.reason.trim(),
      params.categoryId ?? null,
      params.categoryLabel ?? null,
      params.payableBillId ?? null,
      u.id,
      moment()
    );
    await cashMovementRepository.save(movement);
    setMovements((prev) => [...prev, movement]);
  }, []);

  const value = useMemo(
    () => ({
      featureEnabled,
      disabledBySettings,
      loading,
      session,
      sessionExpired,
      canManage,
      movements,
      sells,
      refresh,
      openSession,
      closeSession,
      addEntry,
      addWithdrawal,
    }),
    [
      featureEnabled,
      disabledBySettings,
      loading,
      session,
      sessionExpired,
      canManage,
      movements,
      sells,
      refresh,
      openSession,
      closeSession,
      addEntry,
      addWithdrawal,
    ]
  );

  return (
    <CashRegisterContext.Provider value={value}>
      {children}
    </CashRegisterContext.Provider>
  );
}

export function useCashRegister(): CashRegisterContextValue {
  const ctx = useContext(CashRegisterContext);
  if (!ctx) {
    throw new Error("useCashRegister deve ser usado dentro de CashRegisterProvider.");
  }
  return ctx;
}
